import {createApp} from 'vue'
import App from './App.vue'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import router from './router'
import store from './store'
import http from '@/http'
import i18n from '@/i18n'
import I18nService from '@/services/I18nService'
import AuthService from '@/services/AuthService'
import UserService from '@/services/UserService'
import StakeholderService from '@/services/StakeholderService';
import LineIdService from '@/services/LineIdService';
import PlanningService from '@/services/PlanningService';
import OfficeService from '@/services/OfficeService';
import SiteService from '@/services/SiteService';
import AppointmentService from '@/services/AppointmentService';
import RequestService from '@/services/RequestService';
import BarcodeService from '@/services/BarcodeService';
import ApplicationService from '@/services/ApplicationService';
import ControlService from '@/services/ControlService';
import ImmediateTaggingService from '@/services/ImmediateTaggingService';
import SpecialRequestService from '@/services/SpecialRequestService';
import AddressService from '@/services/AddressService';
import ExportService from '@/services/ExportService';
import RoamingTeamService from '@/services/RoamingTeamService';
import authStore from '@/store/modules/auth.store'
import i18nStore from '@/store/modules/i18n.store';
import settingStore from '@/store/modules/setting.store';
import authGuard from '@/router/guards/auth.guard'
import roleGuard from '@/router/guards/role.guard';
import maintenanceInterceptor from '@/http/interceptors/maintenance.interceptor';
import unauthorizedInterceptor from '@/http/interceptors/unauthorized.interceptor'
import errorInterceptor from '@/http/interceptors/error.interceptor'
import mitt from 'mitt';

import 'primevue/resources/themes/saga-blue/theme.css'
import 'primevue/resources/primevue.min.css'
import 'primeicons/primeicons.css'
import 'primeflex/primeflex.css'
import EquipmentTypeService from '@/services/EquipmentTypeService';
import VenueService from '@/services/VenueService';
import SettingsService from '@/services/SettingsService';

const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:9000/api' : window.origin + window.location.pathname + 'api'
const authService = new AuthService(http, API_URL)
const userService = new UserService(http, API_URL)
const stakeHolderService = new StakeholderService(http, API_URL)
const lineIdService = new LineIdService(http, API_URL)
const i18nService = new I18nService(http, API_URL)
const officeService = new OfficeService(http, API_URL)
const siteService = new SiteService(http, API_URL)
const appointmentService = new AppointmentService(http, API_URL)
const controlService = new ControlService(http, API_URL)
const planningService = new PlanningService(http, API_URL)
const requestService = new RequestService(http, API_URL)
const barcodeService = new BarcodeService(http, API_URL)
const applicationService = new ApplicationService(http, API_URL)
const immediateTaggingService = new ImmediateTaggingService(http, API_URL);
const specialRequestService = new SpecialRequestService(http, API_URL);
const exportService = new ExportService(http, API_URL);
const equipmentTypeService = new EquipmentTypeService(http, API_URL);
const roamingTeamService = new RoamingTeamService(http, API_URL);
const venueService = new VenueService(http, API_URL);
const settingsService = new SettingsService(http, API_URL);

const emitter = mitt();

router.beforeEach(console.debug)
router.beforeEach(authGuard(store))
router.beforeEach(roleGuard(store))
store.registerModule('auth', authStore(http, router, authService, userService))
store.registerModule('i18n', i18nStore(i18n, i18nService))
store.registerModule('setting', settingStore(applicationService));

setup()

async function setup() {
  await store.dispatch('auth/recover')
  await store.dispatch('setting/init')
  http.interceptors.response.use(
    response => response,
    maintenanceInterceptor(router)
  )
  http.interceptors.response.use(
    response => response,
    unauthorizedInterceptor(store, router, emitter)
  )
  http.interceptors.response.use(
    response => response,
    errorInterceptor(emitter)
  )

  const app = createApp(App)
    .use(store)
    .use(router)
    .use(i18n)
    .use(PrimeVue)
    .use(ToastService);

  app.config.unwrapInjectedRef = true
  app.config.globalProperties.$i18nService = i18nService
  app.config.globalProperties.$authService = authService
  app.config.globalProperties.$userService = userService
  app.config.globalProperties.$stakeHolderService = stakeHolderService
  app.config.globalProperties.$lineIdService = lineIdService
  app.config.globalProperties.$officeService = officeService
  app.config.globalProperties.$siteService = siteService
  app.config.globalProperties.$appointmentService = appointmentService
  app.config.globalProperties.$planningService = planningService
  app.config.globalProperties.$requestService = requestService
  app.config.globalProperties.$barcodeService = barcodeService
  app.config.globalProperties.$applicationService = applicationService
  app.config.globalProperties.$controlService = controlService
  app.config.globalProperties.$immediateTaggingService = immediateTaggingService;
  app.config.globalProperties.$specialRequestService = specialRequestService
  app.config.globalProperties.$exportService = exportService
  app.config.globalProperties.$roamingTeamService = roamingTeamService

  // Instantiates the address resolution service
  const addressApiUrl = store.getters['setting/getAddressApiUrl'];
  app.config.globalProperties.$addressService = new AddressService(http, addressApiUrl)

  app.config.globalProperties.$emitter = emitter
  app.config.globalProperties.$equipmentTypeService = equipmentTypeService
  app.config.globalProperties.$venueService = venueService
  app.config.globalProperties.$settingsService = settingsService
  app.mount('#app')
}

